import {
  useGetDatasetInferencesQuery,
  useGetImageAnnotationLazyQuery,
  useGetImageInferenceLazyQuery,
  type Inference,
  type MLInference,
} from '@generated/graphql';
import { generateColorTriad } from '@helper-functions/color';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type PropsWithChildren,
} from 'react';
import {
  GroundTruthSettings,
  ImageAnnotations,
  InferenceSettings,
} from './DatasetsDetailsImagesSliderTypes';

interface DatasetsDetailsImagesSliderContextProps {
  datasetInferences: MLInference[];
  groundTruthData: ImageAnnotations[];
  groundTruthSettings: GroundTruthSettings;
  inferenceSettings: InferenceSettings[];
  inferenceData: Record<string, Inference[]>;
  addInferenceLayer: () => void;
  removeInferenceLayer: (index: number) => void;
  updateGroundTruthSetting: (
    key: keyof GroundTruthSettings,
    value: GroundTruthSettings[keyof GroundTruthSettings],
  ) => void;
  updateInferenceSetting: (
    index: number,
    key: keyof InferenceSettings,
    value: InferenceSettings[keyof InferenceSettings],
  ) => void;
}

const DatasetsDetailsImagesSliderOverlayContext = createContext<
  DatasetsDetailsImagesSliderContextProps | undefined
>(undefined);

type DatasetsDetailsImagesSliderOverlayContextProviderProps = PropsWithChildren<{
  datasetId: string;
  filename: string;
  initialInferenceId: string;
  workspaceId: string;
}>;

export const DatasetsDetailsImagesSliderOverlayContextProvider = ({
  datasetId,
  filename,
  initialInferenceId,
  workspaceId,
  children,
}: DatasetsDetailsImagesSliderOverlayContextProviderProps) => {
  const [getImageAnnotation] = useGetImageAnnotationLazyQuery();
  const [getImageInference] = useGetImageInferenceLazyQuery();
  const { data } = useGetDatasetInferencesQuery({ variables: { workspaceId, datasetId } });
  const datasetInferences = useMemo(() => data?.getMLInferences || [], [data]);

  const [groundTruthSettings, setGroundTruthSettings] = useState<GroundTruthSettings>(
    defaultGroundTruthSettings,
  );
  const [groundTruthData, setGroundTruthData] = useState<ImageAnnotations[]>([]);
  const [inferenceSettings, setInferenceSettings] = useState<InferenceSettings[]>(() =>
    initialInferenceId
      ? [
          {
            ...defaultInferenceSettings,
            inferenceId: initialInferenceId,
            color: generateColorTriad(groundTruthSettings.color)[0],
          },
        ]
      : [],
  );
  const [inferenceData, setInferenceData] = useState<Record<string, Inference[]>>({});

  const updateGroundTruthData = useCallback(async () => {
    const { data: getImageAnnotationData } = await getImageAnnotation({
      variables: { filename, datasetId, workspaceId },
    });
    try {
      const parsedData = JSON.parse(
        getImageAnnotationData?.getImageAnnotation?.data?.[0] || '[]',
      ) as ImageAnnotations[];
      setGroundTruthData(parsedData);
    } catch (e) {
      console.error(e);
    }
  }, [getImageAnnotation, filename, datasetId, workspaceId]);

  const updateInferenceData = useCallback(
    async (inferenceId: string) => {
      const { data: getImageInferenceData } = await getImageInference({
        variables: { filename, inferenceId, workspaceId },
      });
      try {
        setInferenceData((prev) => ({
          ...prev,
          [inferenceId]: getImageInferenceData?.getImageInference?.inferences || [],
        }));
      } catch (e) {
        console.error(e);
      }
    },
    [getImageInference, filename, workspaceId],
  );

  useEffect(() => {
    if (!filename) return;
    setGroundTruthData([]);
    setInferenceData({});
    void updateGroundTruthData();
    void Promise.all(inferenceSettings.map((s) => updateInferenceData(s.inferenceId)));
    // `inferenceSettings` is intentionally omitted — Effect 2 handles inference sync on
    // settings changes. Re-firing here on settings changes would duplicate work and wipe
    // ground truth on every layer add/remove.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filename, updateGroundTruthData, updateInferenceData]);

  useEffect(() => {
    if (!inferenceSettings?.length) return;
    void Promise.all(inferenceSettings.map((s) => updateInferenceData(s.inferenceId)));
  }, [inferenceSettings, updateInferenceData]);

  const addInferenceLayer = useCallback(() => {
    setInferenceSettings((currentLayers) => {
      if (currentLayers.length >= 2) return currentLayers;
      return [
        ...currentLayers,
        {
          ...defaultInferenceSettings,
          color: generateColorTriad(groundTruthSettings.color)[currentLayers.length],
        },
      ];
    });
  }, [groundTruthSettings.color]);

  const removeInferenceLayer = useCallback((index: number) => {
    setInferenceSettings((currentLayers) => currentLayers.filter((_, i) => i !== index));
  }, []);

  const updateGroundTruthSetting = useCallback(
    (key: keyof GroundTruthSettings, value: GroundTruthSettings[keyof GroundTruthSettings]) => {
      setGroundTruthSettings((current) => ({ ...current, [key]: value }));
    },
    [],
  );

  const updateInferenceSetting = useCallback(
    (
      index: number,
      key: keyof InferenceSettings,
      value: InferenceSettings[keyof InferenceSettings],
    ) => {
      setInferenceSettings((current) => {
        const newSettings = [...current];
        newSettings[index] = { ...newSettings[index], [key]: value };
        return newSettings;
      });
    },
    [],
  );

  const value = useMemo(
    () => ({
      datasetInferences,
      groundTruthSettings,
      inferenceData,
      inferenceSettings,
      groundTruthData,
      addInferenceLayer,
      removeInferenceLayer,
      updateGroundTruthSetting,
      updateInferenceSetting,
    }),
    [
      datasetInferences,
      groundTruthSettings,
      inferenceData,
      inferenceSettings,
      groundTruthData,
      addInferenceLayer,
      removeInferenceLayer,
      updateGroundTruthSetting,
      updateInferenceSetting,
    ],
  );

  return (
    <DatasetsDetailsImagesSliderOverlayContext.Provider value={value}>
      {children}
    </DatasetsDetailsImagesSliderOverlayContext.Provider>
  );
};

export const useImageOverlayContext = () => {
  return useContext(DatasetsDetailsImagesSliderOverlayContext);
};

const defaultGroundTruthSettings: GroundTruthSettings = {
  mode: '2d',
  color: '#ff0000',
  opacity: 1,
  strokeWidth: 2,
  visible: false,
};

const defaultInferenceSettings: InferenceSettings = {
  color: '#00ff00',
  confidenceThreshold: 50,
  inferenceId: '',
  opacity: 1,
  showLabels: false,
  strokeWidth: 2,
  visible: true,
};
